import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, btnPrimary, fmtDate } from "./shared";

export function DuplicateMerge({ onMerged }) {
  const [items, setItems] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/duplicates").then((res) => setItems(res.data.items)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const merge = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/duplicates/merge");
      toast.success(`Slått sammen: ${res.data.slettet} kontoer slettet, ${res.data.flyttet} kuponger flyttet`);
      setConfirm(false);
      load();
      onMerged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!items.length) return null;
  return (
    <Card
      className="mt-6 border-amber-400/30"
      title={`${items.length} telefonnummer har flere kontoer`}
      icon={Users}
      testid="duplicates-card"
      action={<button onClick={() => setConfirm(true)} className={btnPrimary} data-testid="merge-duplicates-btn">Slå sammen</button>}
    >
      <p className="mb-4 text-sm text-[#A8A29E]">
        Den nyeste kontoen beholdes. Kampanjekupongene fra de andre flyttes over, og de ekstra kontoene slettes (en kopi lagres).
      </p>
      <div className="space-y-3">
        {items.map((g) => (
          <div key={g.telefon} className="rounded-xl border border-white/10 p-3 text-sm" data-testid={`duplicate-${g.telefon}`}>
            <p className="font-semibold text-[#FDFBF7]">{g.telefon}</p>
            {g.kontoer.map((k) => (
              <p key={k.id} className={k.id === g.beholdes ? "text-[#E5C564]" : "text-[#A8A29E] line-through"}>
                {k.fornavn} · {k.epost} · {fmtDate(k.opprettet)} · {k.kampanjekuponger} kampanjekuponger
                {k.id === g.beholdes ? " (beholdes)" : ""}
              </p>
            ))}
          </div>
        ))}
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-white/10 bg-[#282421] text-[#FDFBF7]">
          <AlertDialogHeader>
            <AlertDialogTitle>Slå sammen {items.length} telefonnummer?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A8A29E]">
              De overstrøkne kontoene slettes, og velkomstkupongene deres forsvinner. Kampanjekupongene flyttes til kontoen som beholdes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="merge-cancel-btn">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); merge(); }} disabled={busy} data-testid="merge-confirm-btn" className="bg-[#D4AF37] text-[#1A1715] hover:bg-[#E5C564]">
              {busy ? "Slår sammen…" : "Ja, slå sammen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
